import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
  withNoXsrfProtection,
} from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, inject, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { ServiceWorkerModule } from '@angular/service-worker';
import { bcpSafeRequestInterceptor } from '@cix-software-labs/js-networking';
import { firstValueFrom } from 'rxjs';
import { appRoutes } from './app-routing.module';
import { AppComponent } from './app.component';
import { ApiHeadersInterceptor } from './core/interceptors/api-headers.interceptor';
import { errorHandlerInterceptor } from './core/interceptors/error-handler.interceptor';
import { RepositoryModule as TransactionRepositoryModule } from './feature/admin/transactions/infrastructure/repository/repository.module';
import { AuthHttpRepository } from './feature/auth/infrastructure/repository';
import { AUTH_REPOSITORY } from './feature/auth/infrastructure/repository/repository.module';
import { TranslationService } from './shared/services/translation/translation.service';
import { SharedModule } from './shared/shared.module';

// MSAL import
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';
import {
  MSALGuardConfigFactory,
  MSALInstanceFactory,
  MSALInterceptorConfigFactory,
} from './core/config/msal-config';
import { provideContactRepository } from './feature/admin/contacts';
import { provideGeolocationRepository } from './feature/admin/geolocation/geolocation.provider';
import { provideProfileRepository } from './feature/admin/profile/profile.providers';
//ng-networking imports
import { provideRouter, withComponentInputBinding, withViewTransitions } from '@angular/router';
import { bcpProvideHttpClient, withEncryption } from '@cix-software-labs/ng-networking';
import { provideOtpRepository } from './feature/otp/otp.provider';
import { provideCaptchaRepository } from './feature/recaptcha-v3/captcha.provider';
import { ConfigHttpService } from './shared/services/config-http.service';

export async function initializeApp(): Promise<void> {
  const translationService = inject(TranslationService);
  await firstValueFrom(translationService.loadTranslations('es'));
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    // AppRoutingModule,
    SharedModule,
    TransactionRepositoryModule,
    MsalModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: false, // Disabled temporarily due to CSP conflicts
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers: [
    provideHttpClient(
      withNoXsrfProtection(),
      withInterceptors([bcpSafeRequestInterceptor(), errorHandlerInterceptor]),
      withInterceptorsFromDi() // Includes class-based interceptors: MsalInterceptor, ApiHeadersInterceptor
    ),
    provideRouter(appRoutes, withComponentInputBinding(), withViewTransitions()),
    provideAppInitializer(initializeApp),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiHeadersInterceptor,
      multi: true,
    },
    {
      provide: AUTH_REPOSITORY,
      useClass: AuthHttpRepository,
    },
    // MSAL Providers
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },

    bcpProvideHttpClient([withEncryption(ConfigHttpService), withInterceptorsFromDi()]),
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideContactRepository(),
    provideProfileRepository(),
    provideGeolocationRepository(),
    provideOtpRepository(),
    provideCaptchaRepository(),
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
